import {
  ComponentType,
  ReactNode,
  SVGProps,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { Button } from '@progress/kendo-react-buttons';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import { Tooltip } from '@progress/kendo-react-tooltip';

import { useMediaQuery } from '@shared/hooks/useMediaQuery';
import jetsLogo from '@shared/assets/images/jets-logo-orange.svg';
import classes from './AppSidebar.module.scss';

export type SidebarIcon = ComponentType<SVGProps<SVGSVGElement>>;

type SidebarTarget = { to: string } | { href: string } | { onClick: () => void };

export type SidebarSubOption = { key: string; label: string } & SidebarTarget;

interface SidebarOptionBase {
  key: string;
  label: string;
  icon: SidebarIcon;
  active?: boolean;
  mobileOnly?: boolean;
  create?: { label: string; onClick: () => void };
}

export type SidebarOption = SidebarOptionBase & (SidebarTarget | { children: SidebarSubOption[] });

interface AppSidebarProps {
  appLogo: { src: string; alt: string };
  options: SidebarOption[];
  isMobileOpen: boolean;
  onMobileClose: () => void;
  // nonProd paints the rail green, so nobody mistakes a test environment for production.
  variant?: 'default' | 'nonProd';
  children: ReactNode;
}

interface TargetProps {
  target: SidebarTarget;
  className: string;
  ariaLabel?: string;
  onNavigate: () => void;
  children: ReactNode;
}

// every way out of the menu closes it on mobile
const Target = ({ target, className, ariaLabel, onNavigate, children }: TargetProps): JSX.Element => {
  if ('to' in target) {
    return <Link to={target.to} className={className} aria-label={ariaLabel} onClick={onNavigate}>{children}</Link>;
  }
  if ('href' in target) {
    return <a href={target.href} target="_blank" rel="noreferrer" className={className} aria-label={ariaLabel} onClick={onNavigate}>{children}</a>;
  }
  const handleClick = () => {
    target.onClick();
    onNavigate();
  };
  return <button type="button" className={className} aria-label={ariaLabel} onClick={handleClick}>{children}</button>;
};

interface SidebarItemProps {
  option: SidebarOption;
  onNavigate: () => void;
}

const SidebarItem = memo(({ option, onNavigate }: SidebarItemProps): JSX.Element => {
  // remount to close: the pointer never leaves the button when the sidebar collapses
  const [ tooltipKey, setTooltipKey ] = useState(0);
  const { label, icon: Icon, active, create } = option;
  const content = (
    <>
      <span className="icon"><Icon aria-hidden="true" focusable="false" /></span>
      <span className="caption">{label}</span>
    </>
  );

  if ('children' in option) {
    return (
      <div className={cx('item', { current: active })}>
        <PanelBar className="group">
          <PanelBarItem expanded={false} title={content}>
            <li className="subMenu" role="none">
              <ul>
                {option.children.map(({ key, label: subLabel, ...target }) => (
                  <li key={key}>
                    <Target target={target} className="subLink" onNavigate={onNavigate}>{subLabel}</Target>
                  </li>
                ))}
              </ul>
            </li>
          </PanelBarItem>
        </PanelBar>
      </div>
    );
  }

  return (
    <div className={cx('item', { current: active })}>
      <Target target={option} className="link" ariaLabel={label} onNavigate={onNavigate}>{content}</Target>
      {create && (
        <Tooltip key={tooltipKey} style={{ zIndex: 10031 }} openDelay={100} anchorElement="target" position="top">
          <Button
            className="create"
            title={create.label}
            aria-label={create.label}
            onClick={() => {
              setTooltipKey((key) => key + 1);
              create.onClick();
              onNavigate();
            }}
          />
        </Tooltip>
      )}
    </div>
  );
});

const AppSidebar = ({ appLogo, options, isMobileOpen, onMobileClose, variant = 'default', children }: AppSidebarProps): JSX.Element => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    if (!isMobile) {
      onMobileClose();
    }
  }, [ isMobile, onMobileClose ]);

  useEffect(() => {
    if (!isMobileOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [ isMobileOpen ]);

  const closeOnMobile = useCallback(() => {
    if (isMobile) {
      onMobileClose();
    }
  }, [ isMobile, onMobileClose ]);

  const mainOptions = options.filter(({ mobileOnly }) => !mobileOnly);
  const mobileOptions = options.filter(({ mobileOnly }) => mobileOnly);

  return (
    <div className={cx(classes.appSidebar, { nonProd: variant === 'nonProd', mobileOpen: isMobileOpen })}>
      <nav className="rail" aria-label="Main menu">
        <div className="scroll">
          <div className="mobileHeader">
            <div className="logos">
              <img src={jetsLogo} className="jets" alt="Jets.com Logo" />
              <img src={appLogo.src} className="appLogo" alt={appLogo.alt} />
            </div>
            <button type="button" className="dismiss" onClick={onMobileClose} aria-label="Close sidebar" />
          </div>
          <div className="links">
            {mainOptions.map((option) => <SidebarItem key={option.key} option={option} onNavigate={closeOnMobile} />)}
            {mobileOptions.length > 0 && (
              <div className="mobileOnly">
                <hr />
                {mobileOptions.map((option) => <SidebarItem key={option.key} option={option} onNavigate={closeOnMobile} />)}
              </div>
            )}
          </div>
        </div>
      </nav>
      <div className="main">{children}</div>
    </div>
  );
};

export default memo(AppSidebar);
